import Skeleton from './DashboardSkeleton-v1';

const PIPELINE_PERIOD_OPTIONS = [7, 30, 90, 180, 0];

function PipelinePeriodLabel({ period, theme }) {
  const current = PIPELINE_PERIOD_OPTIONS.includes(Number(period)) ? Number(period) : 30;
  const label = current === 0 ? 'Last 365 days' : `${current} days`;

  return (
    <span
      aria-hidden="true"
      style={{
        minHeight: 44,
        padding: '8px 10px',
        borderRadius: 8,
        border: '1px solid transparent',
        background: 'transparent',
        color: theme.textMuted,
        fontSize: 12,
        fontWeight: 700,
        fontFamily: "'DM Sans',sans-serif",
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        lineHeight: 1,
      }}
    >
      <span>{label}</span>
      <span style={{ color: '#e8a830', fontSize: 13 }}>↻</span>
    </span>
  );
}

function getNextPipelinePeriod(period) {
  const current = PIPELINE_PERIOD_OPTIONS.includes(Number(period)) ? Number(period) : 30;
  const currentIndex = PIPELINE_PERIOD_OPTIONS.indexOf(current);
  return PIPELINE_PERIOD_OPTIONS[(currentIndex + 1) % PIPELINE_PERIOD_OPTIONS.length];
}

export default function DashboardPipelineSection({
  period, setPeriod, pipelineStats, pipelineStatsLoading, onRetryPipelineStats,
  lastUpdated, theme, collapsed: statsCollapsed, onToggle: toggleStatsCollapsed,
}) {
  const currentPeriod = PIPELINE_PERIOD_OPTIONS.includes(Number(period)) ? Number(period) : 30;
  const cyclePipelinePeriod = () => setPeriod?.(getNextPipelinePeriod(currentPeriod));
  // Pipeline stats — fetched from get_pipeline_stats RPC.
  const pipelineLoading = pipelineStats === null;
  const pipelineTotal    = pipelineStats?.total    ?? 0;
  const pipelineCount    = pipelineStats?.count    ?? 0;
  const pipelineLargest  = pipelineStats?.largest  ?? 0;
  const pipelineAvg      = pipelineStats?.avg      ?? 0;
  const pipelineDwellings= pipelineStats?.dwellings?? 0;
  const pipelineTrend    = pipelineStats?.trend    ?? null;
  const pipelineRefreshing = Boolean(pipelineStatsLoading && pipelineStats !== null);
  const pipelineFailed = Boolean(pipelineStats?.load_error);
  const fmtPipeline = v => {
    const value = Number(v || 0);
    if (!value) return '$0';
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(value >= 10000000000 ? 0 : 1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(value >= 100000000 ? 0 : 1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toLocaleString()}`;
  };

  return (
    <div style={{marginBottom:statsCollapsed?6:12}}>
      <div
        onClick={toggleStatsCollapsed}
        role="button" aria-expanded={!statsCollapsed} tabIndex={0}
        onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();toggleStatsCollapsed();}}}
        style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",cursor:"pointer",userSelect:"none"}}
      >
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6a80a0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{transform:statsCollapsed?"rotate(0deg)":"rotate(90deg)",transition:"transform 0.2s",flexShrink:0}}>
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <span style={{color:theme.textMuted,fontSize:12,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>Development Pipeline</span>
        </div>
        <span style={{color:"#6a80a0",fontSize:11}}>
          Active Pipeline{pipelineStats?.stale ? ' · last saved' : lastUpdated && ` · ${lastUpdated.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}`}
        </span>
      </div>

      {!statsCollapsed && (
      <>
      {/* Development Pipeline hero tile — v37 gradient */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Cycle pipeline period. Current: ${currentPeriod === 0 ? 'Last 365 days' : `${currentPeriod} days`}`}
        onClick={cyclePipelinePeriod}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            cyclePipelinePeriod();
          }
        }}
        style={{
        background: "linear-gradient(145deg, #3d6275, #081525)",
        border: "1px solid #c0a06033",
        borderRadius: 14, padding: 18, marginBottom: 12,
        boxShadow: "0 4px 24px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.2)",
        cursor: "pointer",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <span style={{color:theme.accent,fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>Pipeline Value</span>
        </div>
        <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:4,flexWrap:"wrap",opacity:pipelineRefreshing?0.55:1,transition:"opacity 0.18s ease"}}>
          {pipelineLoading
            ? <Skeleton width="100px" height="36px" bg="#1a3a50" />
            : <span style={{color:theme.text,fontSize:32,fontWeight:800,fontFamily:"'DM Sans',sans-serif",letterSpacing:-1,lineHeight:1}}>{fmtPipeline(pipelineTotal)}</span>
          }
          {!pipelineLoading && <span style={{color:theme.textMuted,fontSize:12}}>across {pipelineCount} costed DA{pipelineCount!==1?'s':''}</span>}
          {pipelineFailed && <span style={{color:theme.accent,fontSize:11,fontWeight:700}}>Temporarily unavailable</span>}
          {!pipelineLoading && currentPeriod !== 0 && pipelineTrend !== null && pipelineTrend !== 0 && (
            <span style={{color:pipelineTrend > 0 ? "#5aba6a" : theme.accent,fontSize:11,fontWeight:600}}>{pipelineTrend>0?"▲":"▼"} {Math.abs(pipelineTrend)}% vs prior {currentPeriod}d</span>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:2,marginLeft:-10}}>
          <PipelinePeriodLabel period={currentPeriod} theme={theme} />
          {pipelineRefreshing && <span style={{color:theme.textMuted,fontSize:11}}>Updating...</span>}
          {pipelineFailed && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onRetryPipelineStats?.(); }}
              style={{background:"transparent",border:`1px solid ${theme.border}`,borderRadius:999,color:theme.text,fontSize:11,fontWeight:700,padding:"5px 9px",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}
            >
              Retry
            </button>
          )}
        </div>
        {!pipelineLoading && pipelineFailed && (
          <div style={{fontSize:11,color:theme.textMuted,fontStyle:"italic",marginTop:4}}>Live pipeline stats did not load. The dashboard stays usable while SiteScope retries.</div>
        )}
        {!pipelineLoading && !pipelineFailed && pipelineCount === 0 && (
          <div style={{fontSize:11,color:theme.textMuted,fontStyle:"italic",marginTop:4}}>Cost data available once Planning Portal data is ingested.</div>
        )}
        {!pipelineLoading && pipelineCount > 0 && (
          <div style={{display:"flex",gap:24,marginTop:8}}>
            <div>
              <div style={{color:"#6a80a0",fontSize:10,textTransform:"uppercase",letterSpacing:0.5}}>Avg / costed DA</div>
              <div style={{color:theme.text,fontSize:14,fontWeight:700}}>{fmtPipeline(pipelineAvg)}</div>
            </div>
            <div>
              <div style={{color:"#6a80a0",fontSize:10,textTransform:"uppercase",letterSpacing:0.5}}>Largest</div>
              <div style={{color:theme.text,fontSize:14,fontWeight:700}}>{fmtPipeline(pipelineLargest)}</div>
            </div>
            {pipelineDwellings > 0 && (
              <div>
                <div style={{color:"#6a80a0",fontSize:10,textTransform:"uppercase",letterSpacing:0.5}}>New Dwellings</div>
                <div style={{color:theme.text,fontSize:14,fontWeight:700}}>{pipelineDwellings}</div>
              </div>
            )}
          </div>
        )}
      </div>
      </>
    )}
    </div>

  );
}
